import { useId, type FormEvent } from "react";
import { BUCKET_KINDS, type Bucket, type BucketInput, type BucketKind } from "@/api/finance";
import { RequestError } from "@/components/RequestError";
import { SelectField, TextAreaField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type BucketFormProps = {
  initial: Bucket | null;
  nextSortOrder: number;
  submitLabel: string;
  onSubmit: (input: BucketInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

export function BucketForm({
  initial,
  nextSortOrder,
  submitLabel,
  onSubmit,
  onCancel,
  onReload,
}: BucketFormProps) {
  const id = useId();
  const { values, set } = useFormValues({
    name: initial?.name ?? "",
    kind: initial?.kind ?? ("pool" as BucketKind),
    default_percent: initial ? String(Number(initial.default_percent)) : "",
    default_payee: initial?.default_payee ?? "",
    purpose: initial?.purpose ?? "",
    monthly_limit_inr: initial?.monthly_limit_inr ?? "",
    sort_order: String(initial?.sort_order ?? nextSortOrder),
  });
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        name: values.name.trim(),
        kind: values.kind,
        default_percent: values.default_percent.trim() || "0",
        default_payee: values.default_payee.trim(),
        purpose: values.purpose.trim(),
        monthly_limit_inr: values.monthly_limit_inr.trim() || null,
        sort_order: Number(values.sort_order) || 0,
        archived: initial?.archived ?? false,
      }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-3">
        <TextField
          id={`${id}-name`}
          label="Name"
          value={values.name}
          onChange={(value) => set("name", value)}
          error={errors.name}
        />
        <SelectField
          id={`${id}-kind`}
          label="Kind"
          value={values.kind}
          onChange={(value) => set("kind", value as BucketKind)}
          options={BUCKET_KINDS}
          error={errors.kind}
        />
        <TextField
          id={`${id}-percent`}
          label="Default share %"
          inputMode="decimal"
          value={values.default_percent}
          onChange={(value) => set("default_percent", value)}
          error={errors.default_percent}
          hint="Of every new invoice"
        />
        <TextField
          id={`${id}-payee`}
          label="Usually paid to"
          value={values.default_payee}
          onChange={(value) => set("default_payee", value)}
          error={errors.default_payee}
          hint="Optional"
        />
        <TextField
          id={`${id}-limit`}
          label="Monthly limit (INR)"
          inputMode="decimal"
          value={values.monthly_limit_inr}
          onChange={(value) => set("monthly_limit_inr", value)}
          error={errors.monthly_limit_inr}
          hint="Leave empty to use the bucket's share of the planned monthly income"
        />
        <TextField
          id={`${id}-order`}
          label="Order"
          inputMode="numeric"
          value={values.sort_order}
          onChange={(value) => set("sort_order", value)}
          error={errors.sort_order}
        />
      </div>
      <TextAreaField
        id={`${id}-purpose`}
        label="What it is for"
        value={values.purpose}
        onChange={(value) => set("purpose", value)}
        error={errors.purpose}
        rows={2}
      />
      <FormActions saving={saving} submitLabel={submitLabel} onCancel={onCancel} />
    </form>
  );
}
